/**
 * Centralized React Query cache configuration
 *
 * This file defines data-type-specific cache strategies to optimize
 * network usage, perceived performance, and memory management.
 *
 * Cache Strategy by Data Type:
 * - Static data (complexes, companies): Long stale time, infrequent refetch
 * - Dynamic data (documents, processing status): Short stale time, frequent refetch
 * - Statistics: Medium stale time with automatic refresh
 * - User-specific: Invalidate on auth changes
 */

// Time constants for readability
const SECONDS = 1000;
const MINUTES = 60 * SECONDS;

/**
 * Cache configurations by data type
 *
 * staleTime: How long data is considered fresh (won't refetch)
 * gcTime: How long to keep inactive data in cache before garbage collection
 * refetchInterval: Auto-refetch interval (false = disabled)
 * refetchOnMount: Whether to refetch when component mounts
 * refetchOnWindowFocus: Whether to refetch when window regains focus
 */
export const queryConfig = {
  /**
   * Static data that rarely changes (complexes, company info)
   * - Long stale time since names/addresses don't change often
   * - Keep in cache longer for instant back-navigation
   */
  static: {
    staleTime: 10 * MINUTES,
    gcTime: 30 * MINUTES,
    refetchOnMount: false as const,
    refetchOnWindowFocus: false as const,
  },

  /**
   * Statistics and aggregated data (counts, charts)
   * - Medium stale time since stats update periodically
   * - Auto-refresh for dashboards that stay open
   */
  statistics: {
    staleTime: 2 * MINUTES,
    gcTime: 10 * MINUTES,
    refetchOnMount: true as const,
    refetchOnWindowFocus: true as const,
  },

  /**
   * Document lists and filters
   * - Shorter stale time since new documents arrive frequently
   * - Refetch on focus to catch updates made in other tabs
   */
  documents: {
    staleTime: 1 * MINUTES,
    gcTime: 5 * MINUTES,
    refetchOnMount: true as const,
    refetchOnWindowFocus: true as const,
  },

  /**
   * Processing/active documents (status can change any moment)
   * - Very short stale time for documents being processed
   * - Auto-refresh interval for monitoring active processing
   */
  processing: {
    staleTime: 30 * SECONDS,
    gcTime: 2 * MINUTES,
    refetchOnMount: true as const,
    refetchOnWindowFocus: true as const,
    refetchInterval: 30 * SECONDS, // Auto-poll for status updates
  },

  /**
   * Admin/super-admin statistics
   * - Already has config in useAdminStats, kept for consistency
   */
  admin: {
    staleTime: 1 * MINUTES,
    gcTime: 5 * MINUTES,
    refetchInterval: 5 * MINUTES,
    refetchOnMount: true as const,
    refetchOnWindowFocus: true as const,
  },

  /**
   * Company/user lists (admin views)
   * - Medium stale time since new companies/users are infrequent
   */
  lists: {
    staleTime: 5 * MINUTES,
    gcTime: 15 * MINUTES,
    refetchOnMount: false as const,
    refetchOnWindowFocus: false as const,
  },

  /**
   * Single document detail view
   * - Shorter stale time to catch status updates
   * - But no auto-refresh since user is actively viewing
   */
  detail: {
    staleTime: 1 * MINUTES,
    gcTime: 5 * MINUTES,
    refetchOnMount: true as const,
    refetchOnWindowFocus: true as const,
  },

  /**
   * Infinite query pagination (complexes list with load more)
   * - Longer stale time to preserve scroll position cache
   * - Don't refetch pages that user already scrolled past
   */
  paginated: {
    staleTime: 5 * MINUTES,
    gcTime: 15 * MINUTES,
    refetchOnMount: false as const,
    refetchOnWindowFocus: false as const,
  },

  /**
   * Standard query config (Item 17 - dataService optimization)
   * - Balanced stale time for general data fetching
   * - Refetch on mount and window focus for fresh data
   */
  standard: {
    staleTime: 2 * MINUTES,
    gcTime: 10 * MINUTES,
    refetchOnMount: true as const,
    refetchOnWindowFocus: true as const,
  },

  /**
   * Search query config (Item 17 - dataService optimization)
   * - Shorter stale time since search results should be fresh
   * - No auto-refresh since user is actively searching
   */
  search: {
    staleTime: 30 * SECONDS,
    gcTime: 2 * MINUTES,
    refetchOnMount: false as const,
    refetchOnWindowFocus: false as const,
  },
} as const;

/**
 * Type for query config keys
 */
export type QueryConfigKey = keyof typeof queryConfig;
